import { config } from "@/shared/lib/config";
import { toISOForAPI } from "@/shared/lib/date-time";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { SmartFileUpload } from "@/shared/ui/SmartFileUpload";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormCheckbox,
  FormDatePicker,
  FormInput,
  FormRoot,
  FormSelect,
  FormTextarea,
  Input,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import {
  FileIcon,
  PlusIcon,
  SettingsIcon,
  SparklesIcon,
  TrashIcon,
} from "lucide-react";
import { nanoid } from "nanoid";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { fetcher } from "@/shared/lib/fetcher";
import { isEmploymentContractTypeName } from "@/modules/contracts/domain/contract-type-kind";
import { useContractTypesQuery } from "../api/contract-type-queries";
import { ContractTypeManagementModal } from "./ContractTypeManagementModal";
import { contractFormSamples } from "./contractFormSamples";

type ContractTypeItem = { id: string; name: string };
type DepartmentItem = { id: string; name: string };
type ContractMode = "employment" | "other";
export type ContractFormDocument = {
  id: string;
  title: string;
  fileKey: string | null;
  documentType: "main" | "attachment";
  notes?: string | null;
};

const ContractFormSchema = z.object({
  contractNumber: z.string().optional(),
  title: z.string().min(1, "ກະລຸນາໃສ່ຊື່ສັນຍາ"),
  typeId: z.string().min(1, "ກະລຸນາເລືອກປະເພດສັນຍາ"),
  status: z.enum([
    "active",
    "expiring_soon",
    "expired",
    "renewing",
    "terminated",
    "draft",
  ]),
  counterparty: z.string().min(1, "ກະລຸນາໃສ່ຄູ່ສັນຍາ"),
  counterpartyContact: z.string().optional(),
  customerId: z.string().nullable().optional(),
  quotationDocumentId: z.string().nullable().optional(),
  departmentId: z
    .union([z.string(), z.null()])
    .transform((val) => (val === "" ? null : val))
    .nullable()
    .optional(),
  employeeId: z
    .union([z.string(), z.null()])
    .transform((val) => (val === "" ? null : val))
    .nullable()
    .optional(),
  responsiblePersonId: z
    .union([z.string(), z.null()])
    .transform((val) => (val === "" ? null : val))
    .nullable()
    .optional(),
  startDate: z.date(),
  endDate: z.date().optional(),
  autoRenewal: z.boolean(),
  renewalNoticeDays: z
    .any()
    .transform((val) => {
      if (val === "" || val === null || val === undefined) return 30;
      const num = typeof val === "string" ? Number(val) : val;
      if (typeof num === "number" && !Number.isNaN(num)) {
        return Math.floor(num);
      }
      return 30;
    })
    .refine((val): val is number => typeof val === "number" && val >= 1, {
      message: "ກະລຸນາໃສ່ຈຳນວນວັນທີ່ຖືກຕ້ອງ (ຕ້ອງເປັນຈຳນວນບວກ)",
    }),
  notes: z.string().optional(),
  warnings: z.string().optional(),
  specialConditions: z.string().optional(),
});
export type ContractFormValues = z.infer<typeof ContractFormSchema>;

function titleFromUploadKey(fileKey: string, currentTitle: string): string {
  if (currentTitle.trim()) return currentTitle;
  const segment = fileKey.split("/").pop()?.split("?")[0] ?? "";
  if (!segment) return "";
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

export function ContractForm({
  initialValues,
  initialDocuments,
  onSubmit,
  submitting,
}: {
  initialValues?: Partial<
    ContractFormValues & { startDate?: string; endDate?: string }
  >;
  /** When set (e.g. edit page), hydrate ໄຟລ໌ທີ່ບັນທຶກແລ້ວ */
  initialDocuments?: ContractFormDocument[];
  onSubmit: (
    values: ContractFormValues & { documents: ContractFormDocument[] },
  ) => void;
  submitting?: boolean;
}) {
  const [documents, setDocuments] = useState<ContractFormDocument[]>(
    () => initialDocuments ?? [],
  );
  const [sampleIndex, setSampleIndex] = useState(0);
  const isDev =
    import.meta.hot !== undefined || import.meta.env?.MODE === "development";

  const methods = RHF.useForm<ContractFormValues>({
    resolver: zodResolver(ContractFormSchema),
    defaultValues: {
      contractNumber: "",
      title: "",
      typeId: "",
      status: "draft" as const,
      counterparty: "",
      counterpartyContact: "",
      customerId: null,
      quotationDocumentId: null,
      departmentId: null,
      employeeId: null,
      responsiblePersonId: null,
      startDate: new Date(),
      endDate: undefined,
      autoRenewal: false,
      renewalNoticeDays: 30,
      notes: "",
      warnings: "",
      specialConditions: "",
      ...(initialValues
        ? {
            ...initialValues,
            startDate: initialValues.startDate
              ? new Date(initialValues.startDate)
              : new Date(),
            endDate: initialValues.endDate
              ? new Date(initialValues.endDate)
              : undefined,
          }
        : {}),
    },
  });

  const customerId = methods.watch("customerId");
  const typeId = methods.watch("typeId");
  const endDate = methods.watch("endDate");
  const startDate = methods.watch("startDate");
  const hasEndDateWarning = !endDate;
  const typesQuery = useContractTypesQuery();
  const [contractMode, setContractMode] = useState<ContractMode>("other");

  const selectedType = useMemo(() => {
    const id = typeof typeId === "string" ? typeId : "";
    if (!id) return null;
    return (typesQuery.data ?? []).find((t) => t.id === id) ?? null;
  }, [typeId, typesQuery.data]);

  const isEmploymentMode = contractMode === "employment";

  useEffect(() => {
    if (!selectedType?.name) return;
    setContractMode(
      isEmploymentContractTypeName(selectedType.name) ? "employment" : "other",
    );
  }, [selectedType?.name]);

  useEffect(() => {
    if (!isEmploymentMode) {
      methods.setValue("employeeId", null);
    }
  }, [isEmploymentMode, methods]);

  useEffect(() => {
    if (!isEmploymentMode) return;
    methods.setValue("customerId", null);
    methods.setValue("quotationDocumentId", null);
    methods.setValue("autoRenewal", false);
  }, [isEmploymentMode, methods]);

  // เมื่อเลือกลูกค้าจาก master ให้ auto-fill ຄູ່ສັນຍາ และ ຂໍ້ມູນຕິດຕໍ່ (ບໍ່ໃຊ້ກັບສັນຍາຈ້າງ)
  useEffect(() => {
    if (isEmploymentMode) return;
    const id = typeof customerId === "string" ? customerId?.trim() : null;
    if (!id) return;
    fetcher
      .get<{ id: string; name: string; address?: string | null; contact?: string | null }>(
        `${config.apiUrl}/quotation-invoice/customers/${id}`,
      )
      .then((customer) => {
        if (customer?.name) methods.setValue("counterparty", customer.name);
        if (customer?.contact != null) methods.setValue("counterpartyContact", customer.contact ?? "");
      })
      .catch(() => {});
  }, [customerId, isEmploymentMode, methods]);

  const handleAddMainDocument = () => {
    const newDoc: ContractFormDocument = {
      id: nanoid(),
      title: "",
      fileKey: null,
      documentType: "main",
    };
    setDocuments([...documents, newDoc]);
  };

  const handleAddAttachment = () => {
    const newDoc: ContractFormDocument = {
      id: nanoid(),
      title: "",
      fileKey: null,
      documentType: "attachment",
    };
    setDocuments([...documents, newDoc]);
  };

  const handleUpdateDocument = (
    index: number,
    updates: Partial<Omit<ContractFormDocument, "id">>,
  ) => {
    const newDocs = [...documents];
    const existing = newDocs[index];
    if (existing) {
      newDocs[index] = {
        id: existing.id,
        title: updates.title ?? existing.title,
        fileKey: updates.fileKey ?? existing.fileKey,
        documentType: updates.documentType ?? existing.documentType,
        notes: updates.notes ?? existing.notes,
      };
    }
    setDocuments(newDocs);
  };

  const handleDeleteDocument = (index: number) => {
    setDocuments(documents.filter((_, i) => i !== index));
  };

  const handleAutoFill = () => {
    const sample = contractFormSamples[sampleIndex];
    if (sample) {
      Object.entries(sample).forEach(([key, value]) => {
        // Skip empty strings for required fields - user needs to select these manually
        if (
          (key === "typeId" ||
            key === "departmentId" ||
            key === "employeeId" ||
            key === "responsiblePersonId") &&
          (value === "" || value === null)
        ) {
          return;
        }
        // Handle date fields - convert string to Date
        if (key === "startDate" || key === "endDate") {
          if (value && typeof value === "string") {
            methods.setValue(
              key as keyof ContractFormValues,
              new Date(value) as never,
            );
          }
          return;
        }
        // Handle other fields
        if (value !== undefined && value !== null) {
          methods.setValue(key as keyof ContractFormValues, value as never);
        }
      });
      setSampleIndex((prev) => (prev + 1) % contractFormSamples.length);
      toast.success(
        "Auto-filled form with sample data. Please fill in required fields (ປະເພດສັນຍາ, ພະແນກ, ຜູ້ຮັບຜິດຊອບ)",
      );
    }
  };

  const mainDocuments = documents.filter((d) => d.documentType === "main");
  const attachments = documents.filter((d) => d.documentType === "attachment");

  return (
    <FormRoot<ContractFormValues>
      methods={methods}
      onSubmit={(vals) => {
        onSubmit({
          ...vals,
          startDate: toISOForAPI(vals.startDate) ?? vals.startDate,
          endDate: toISOForAPI(vals.endDate) ?? vals.endDate,
          documents,
        } as ContractFormValues & {
          startDate: string;
          endDate?: string;
          documents: ContractFormDocument[];
        });
      }}
      className="space-y-6"
    >
      {/* Section 1: Contract Information */}
      <Card
        data-tourid="create-form-basic-info"
        data-tourid-edit="edit-form-basic-info"
      >
        <CardHeader>
          <CardTitle className="font-semibold text-sm uppercase tracking-wide">
            ຂໍ້ມູນສັນຍາ
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormInput
            name="title"
            label="ຊື່ສັນຍາ"
            requiredMark
            placeholder={
              isEmploymentMode
                ? "ຕົວຢ່າງ: ສັນຍາຈ້າງພະນັກງານ"
                : "ຕົວຢ່າງ: ສັນຍາໃຫ້ບໍລິການ, NDA, MOU..."
            }
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormInput
              name="contractNumber"
              label="ເລກທີສັນຍາ"
              placeholder="ບໍ່ບັງຄັບ"
            />
            <FormInput
              name="counterpartyContact"
              label="ຂໍ້ມູນຕິດຕໍ່ຄູ່ສັນຍາ"
              placeholder="ເບີໂທ, ອີເມວ (ບໍ່ບັງຄັບ)"
            />
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <div className="mb-2 flex gap-2">
                <Button
                  type="button"
                  variant={isEmploymentMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setContractMode("employment");
                    methods.setValue("typeId", "");
                    methods.setValue("customerId", null);
                    methods.setValue("quotationDocumentId", null);
                    methods.setValue("autoRenewal", false);
                  }}
                >
                  ສັນຍາຈ້າງ
                </Button>
                <Button
                  type="button"
                  variant={!isEmploymentMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setContractMode("other");
                    methods.setValue("typeId", "");
                    methods.setValue("employeeId", null);
                  }}
                >
                  ສັນຍາອື່ນໆ
                </Button>
              </div>
              <FormInfiniteCombobox<ContractTypeItem>
                name="typeId"
                label="ປະເພດສັນຍາ"
                requiredMark
                queryKey={["contract-types", contractMode]}
                queryFn={async (args) => {
                  const result = await fetchLookupForInfinite(
                    `${config.apiUrl}/contracts/types/lookup`,
                    args,
                  );
                  const items = (result.items ?? []).filter((item) =>
                    isEmploymentMode
                      ? isEmploymentContractTypeName(item.name)
                      : !isEmploymentContractTypeName(item.name),
                  );
                  return { ...result, items };
                }}
                preloadQueryFn={(id) =>
                  hydrateLookupItem(`${config.apiUrl}/contracts/types/lookup`, id)
                }
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                placeholder="ເລືອກປະເພດສັນຍາ..."
              />
            </div>
            <ContractTypeManagementButton />
          </div>
          {!isEmploymentMode && (
            <>
              <FormInfiniteCombobox<{ id: string; name: string }>
                name="customerId"
                label="ລູກຄ້າ/ຄູ່ສັນຍາ (ຈາກ master)"
                queryKey={["quotation-invoice-customers"]}
                queryFn={(args) =>
                  fetchLookupForInfinite(
                    `${config.apiUrl}/quotation-invoice/customers`,
                    args,
                  )
                }
                preloadQueryFn={async (id) => {
                  const data = await fetcher.get<{ id: string; name: string }>(
                    `${config.apiUrl}/quotation-invoice/customers/${id}`,
                  );
                  return data ? { id: data.id, name: data.name } : null;
                }}
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                placeholder="ເລືອກລູກຄ້າ (ບໍ່ບັງຄັບ — ຫຼືພິມຄູ່ສັນຍາດ້ວຍມືດ້ານລຸ່ມ)"
                clearable
              />
              <FormInfiniteCombobox<{ id: string; name: string }>
                name="quotationDocumentId"
                label="ອ້າງອີງຈາກເອກະສານລາຍລະອຽດ (ບໍ່ບັງຄັບ)"
                queryKey={["quotation-invoice-documents-lookup"]}
                queryFn={async ({ search, pageParam }) => {
                  const limit = 20;
                  const page = Number(pageParam) || 1;
                  const offset = (page - 1) * limit;
                  const params = new URLSearchParams();
                  params.set("limit", String(limit));
                  params.set("offset", String(offset));
                  if (search?.trim()) {
                    params.set("filters", JSON.stringify([{ field: "search", op: "contains", value: search.trim() }]));
                  }
                  const res = await fetcher.get<{
                    data: Array<{ id: string; documentNumber?: string | null; customerName?: string | null }>;
                    meta: { total: number };
                  }>(`${config.apiUrl}/quotation-invoice?${params.toString()}`);
                  const data = res?.data ?? [];
                  const total = res?.meta?.total ?? 0;
                  const items = data.map((d) => ({
                    id: d.id,
                    name: [d.documentNumber, d.customerName].filter(Boolean).join(" — ") || d.id,
                  }));
                  const nextPage = offset + limit < total ? page + 1 : null;
                  return { items, nextPage };
                }}
                preloadQueryFn={async (id) => {
                  const doc = await fetcher.get<{ id: string; documentNumber?: string | null; customerName?: string | null }>(
                    `${config.apiUrl}/quotation-invoice/${id}`,
                  );
                  if (!doc) return null;
                  return {
                    id: doc.id,
                    name: [doc.documentNumber, doc.customerName].filter(Boolean).join(" — ") || doc.id,
                  };
                }}
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                placeholder="ເລືອກເອກະສານລາຍລະອຽດ (ບໍ່ບັງຄັບ)"
                clearable
              />
            </>
          )}
          <FormInput
            name="counterparty"
            label="ຄູ່ສັນຍາ"
            requiredMark
            placeholder={
              isEmploymentMode
                ? "ຊື່ພະນັກງານຕາມເອກະສານ (ຫຼືເລືອກພະນັກງານດ້ານລຸ່ມແລ້ວກອບປ້ອນເອງ)"
                : "ຊື່ບໍລິສັດ ຫຼື ບຸກຄົນ (ຫຼືເລືອກຈາກ master ດ້ານເທິງ)"
            }
          />
          <div
            className="grid grid-cols-1 gap-4 md:grid-cols-2"
            data-tourid="create-form-administration"
            data-tourid-edit="edit-form-administration"
          >
            <FormInfiniteCombobox<DepartmentItem>
              name="departmentId"
              label="ພະແນກທີ່ກ່ຽວຂ້ອງ"
              queryKey={["departments"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/contracts/departments/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(
                `${config.apiUrl}/contracts/departments/lookup`,
                id,
              )
            }
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
              placeholder="ເລືອກພະແນກ (ບໍ່ບັງຄັບ)"
              clearable
            />
            {isEmploymentMode && (
              <FormInfiniteCombobox<{ id: string; name: string }>
                name="employeeId"
                label="ພະນັກງານທີ່ຜູກກັບສັນຍາ"
                queryKey={["contract-users", "employee"]}
                queryFn={(args) =>
                  fetchLookupForInfinite(
                    `${config.apiUrl}/contracts/users/lookup`,
                    args,
                  )
                }
                preloadQueryFn={(id) =>
                  hydrateLookupItem(
                    `${config.apiUrl}/contracts/users/lookup`,
                    id,
                  )
                }
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                placeholder="ເລືອກພະນັກງານ (ສຳລັບສັນຍາຈ້າງ — ເອກະສານຂອງຂ້ອຍ)"
                clearable
              />
            )}
            <FormInfiniteCombobox<{ id: string; name: string }>
              name="responsiblePersonId"
              label="ຜູ້ຮັບຜິດຊອບ"
              queryKey={["contract-users", "responsible"]}
              queryFn={(args) =>
                fetchLookupForInfinite(
                  `${config.apiUrl}/contracts/users/lookup`,
                  args,
                )
              }
              preloadQueryFn={(id) =>
                hydrateLookupItem(
                  `${config.apiUrl}/contracts/users/lookup`,
                  id,
                )
              }
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
              placeholder="ເລືອກຜູ້ຮັບຜິດຊອບ (ບໍ່ບັງຄັບ)"
              clearable
            />
          </div>
        </CardContent>
      </Card>

      {/* Section 2: Duration & Terms */}
      <Card data-tourid="create-form-duration" data-tourid-edit="edit-form-duration">
        <CardHeader>
          <CardTitle className="font-semibold text-sm uppercase tracking-wide">
            ລະຍະເວລາ & ເງື່ອນໄຂ
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormDatePicker
            name="startDate"
            label="ວັນທີເລີ່ມ"
            requiredMark
            {...(startDate instanceof Date && { defaultMonth: startDate })}
          />
          <div>
            <FormDatePicker
              name="endDate"
              label="ວັນທີຫມົດອາຍຸ"
              {...(endDate instanceof Date && { defaultMonth: endDate })}
            />
            {hasEndDateWarning && (
              <div className="mt-2 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
                ⚠️ ກະລຸນາໃສ່ວັນທີຫມົດອາຍຸເພື່ອຕິດຕາມສັນຍາໄດ້ດີຂຶ້ນ
              </div>
            )}
          </div>
          {!isEmploymentMode && (
            <div className="flex items-end">
              <FormCheckbox name="autoRenewal" label="ຕໍ່ອາຍຸອັດຕະໂນມັດ" />
            </div>
          )}
          <FormInput
            name="renewalNoticeDays"
            label="ແຈ້ງເຕືອນລ່ວງໜ້າ (ວັນ)"
            type="number"
            defaultValue="30"
          />
          <div className="md:col-span-2">
            <FormSelect
              name="status"
              label="ສະຖານະ"
              options={[
                { value: "draft", label: "ຮ່າງ" },
                { value: "active", label: "ກຳລັງໃຊ້ງານ" },
                { value: "expiring_soon", label: "ໃກ້ຫມົດອາຍຸ" },
                { value: "expired", label: "ຫມົດອາຍຸ" },
                { value: "renewing", label: "ກຳລັງຕໍ່ອາຍຸ" },
                { value: "terminated", label: "ສິ້ນສຸດ" },
              ]}
            />
          </div>
        </CardContent>
      </Card>

      {/* Section 3: Document Files */}
      <Card data-tourid="create-form-documents">
        <CardHeader>
          <CardTitle className="font-semibold text-sm uppercase tracking-wide">
            ໄຟລ໌ເອກະສານ
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Main Contract File */}
          <div className="space-y-3 rounded-lg border bg-card p-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">ໄຟລ໌ສັນຍາຫຼັກ</h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddMainDocument}
              >
                <PlusIcon className="mr-2 h-4 w-4" />
                ເພີ່ມໄຟລ໌ສັນຍາ
              </Button>
            </div>
            {mainDocuments.length === 0 ? (
              <div className="rounded-lg border border-dashed p-4 text-center text-muted-foreground text-sm">
                ຍັງບໍ່ມີໄຟລ໌ສັນຍາ
              </div>
            ) : (
              <div className="space-y-2">
                {mainDocuments.map((doc) => {
                  const docIndex = documents.findIndex((d) => d.id === doc.id);
                  return (
                    <div
                      key={doc.id}
                      className="flex items-start gap-3 rounded-lg border p-3"
                    >
                      <FileIcon className="mt-1 h-5 w-5 text-muted-foreground" />
                      <div className="flex-1 space-y-2">
                        <Input
                          type="text"
                          placeholder="ຊື່ເອກະສານ..."
                          value={doc.title}
                          onChange={(e) =>
                            handleUpdateDocument(docIndex, {
                              title: e.target.value,
                            })
                          }
                        />
                        {doc.fileKey ? (
                          <div className="text-muted-foreground text-sm">
                            ອັບໂຫຼດແລ້ວ: {doc.fileKey.split("/").pop()}
                          </div>
                        ) : (
                          <SmartFileUpload
                            keyPrefix="uploads/contracts"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,image/*"
                            onSuccess={(key) =>
                              handleUpdateDocument(docIndex, {
                                fileKey: key,
                                title: titleFromUploadKey(key, doc.title),
                              })
                            }
                            buttonLabel="ອັບໂຫຼດເອກະສານ"
                          />
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteDocument(docIndex)}
                        className="text-destructive hover:text-destructive/80"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Additional Attachments */}
          <div className="space-y-3 rounded-lg border bg-card p-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">ໄຟລ໌ແນບເພີ່ມເຕີມ</h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddAttachment}
              >
                <PlusIcon className="mr-2 h-4 w-4" />
                ເພີ່ມໄຟລ໌ແນບ
              </Button>
            </div>
            {attachments.length === 0 ? (
              <div className="rounded-lg border border-dashed p-4 text-center text-muted-foreground text-sm">
                ຍັງບໍ່ມີໄຟລ໌ແນບ
              </div>
            ) : (
              <div className="space-y-2">
                {attachments.map((doc) => {
                  const docIndex = documents.findIndex((d) => d.id === doc.id);
                  return (
                    <div
                      key={doc.id}
                      className="flex items-start gap-3 rounded-lg border p-3"
                    >
                      <FileIcon className="mt-1 h-5 w-5 text-muted-foreground" />
                      <div className="flex-1 space-y-2">
                        <Input
                          type="text"
                          placeholder="ຊື່ເອກະສານ..."
                          value={doc.title}
                          onChange={(e) =>
                            handleUpdateDocument(docIndex, {
                              title: e.target.value,
                            })
                          }
                        />
                        {doc.fileKey ? (
                          <div className="text-muted-foreground text-sm">
                            ອັບໂຫຼດແລ້ວ: {doc.fileKey.split("/").pop()}
                          </div>
                        ) : (
                          <SmartFileUpload
                            keyPrefix="uploads/contracts"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,image/*"
                            onSuccess={(key) =>
                              handleUpdateDocument(docIndex, {
                                fileKey: key,
                                title: titleFromUploadKey(key, doc.title),
                              })
                            }
                            buttonLabel="ອັບໂຫຼດເອກະສານ"
                          />
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteDocument(docIndex)}
                        className="text-destructive hover:text-destructive/80"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Section 4: Notes */}
      <Card data-tourid="create-form-notes">
        <CardHeader>
          <CardTitle className="font-semibold text-sm uppercase tracking-wide">
            ຫມາຍເຫດ & ເງື່ອນໄຂພິເສດ
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormTextarea
            name="specialConditions"
            label="ເງື່ອນໄຂພິເສດ"
            placeholder="ບໍ່ບັງຄັບ"
            rows={3}
          />
          <FormTextarea
            name="warnings"
            label="ຂໍ້ຄວນລະວັງ"
            placeholder="ບໍ່ບັງຄັບ"
            rows={3}
          />
        </CardContent>
      </Card>

      {/* Spacer for floating buttons */}
      <div className="h-20" />

      {/* Floating Action Bar */}
      <div
        className="sticky bottom-2 flex justify-between border-t bg-background pt-4"
        data-tourid="create-form-submit"
        data-tourid-edit="edit-form-submit"
      >
        {isDev && !initialValues && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAutoFill}
            disabled={submitting}
          >
            <SparklesIcon className="mr-2 h-4 w-4" />
            Auto Fill (Sample {sampleIndex + 1}/{contractFormSamples.length})
          </Button>
        )}
        <div className="ml-auto flex gap-2">
          <Button type="submit" isLoading={submitting} size="lg">
            ບັນທຶກສັນຍາ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}

function ContractTypeManagementButton() {
  const [open, setOpen] = useState(false);
  const typesQuery = useContractTypesQuery();

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="mb-0"
      >
        <SettingsIcon className="h-4 w-4" />
      </Button>
      <ContractTypeManagementModal
        open={open}
        onOpenChange={setOpen}
        types={typesQuery.data ?? []}
        onRefresh={() => typesQuery.refetch()}
      />
    </>
  );
}

// TODO review error create new contract
